/**
 * Teen Patti 20-20 — every sound, synthesised. Same skeleton as
 * andar_bahar/sound.ts (lazy AudioContext, tone()/noise() primitives, a bed
 * bus the duck covers, a lookahead scheduler) plus hold() from aviator for the
 * tanpura drone — the only voice that outlives a one-shot.
 *
 * Bed: "Velvet Durbar" — 92 BPM, E minor pentatonic (E G A B D), a plucked
 * sitar-like lead, a beating tanpura drone on E, and a tabla pattern.
 */

const LS_SOUND = 'tp_sound';
const LS_MUSIC = 'tp_music';
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let soundOn = true, musicOn = true;
let _duckedUntil = 0;

const lsGet = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private */ } };
export function initTpSound() { soundOn = lsGet(LS_SOUND) !== '0'; return soundOn; }
export function isTpSoundOn() { return soundOn; }
export function setTpSoundOn(v: boolean) { soundOn = v; lsSet(LS_SOUND, v ? '1' : '0'); if (!v) stopTpMusic(); else if (musicOn) startTpMusic(); return v; }
export function initTpMusic() { musicOn = lsGet(LS_MUSIC) !== '0'; return musicOn; }
export function isTpMusicOn() { return musicOn; }
export function setTpMusicOn(v: boolean) { musicOn = v; lsSet(LS_MUSIC, v ? '1' : '0'); if (v && soundOn) startTpMusic(); else stopTpMusic(); return v; }

function ac(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ctx = new AC(); master = ctx.createGain(); master.gain.value = 1; master.connect(ctx.destination);
  } catch { ctx = null; }
  return ctx;
}
export function unlockTpSound() { const c = ac(); if (c && c.state === 'suspended' && !document.hidden) void c.resume(); }

function bus(gain: number): { c: AudioContext; g: GainNode } | null {
  if (!soundOn) return null;
  const c = ac(); if (!c || !master) return null;
  const g = c.createGain(); g.gain.value = gain; g.connect(master);
  return { c, g };
}
function tone(freq: number, dur: number, o: { type?: OscillatorType; to?: number; gain?: number; delay?: number } = {}) {
  const b = bus(o.gain ?? 0.08); if (!b) return;
  const t0 = b.c.currentTime + (o.delay ?? 0);
  const osc = b.c.createOscillator(); osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(freq, t0);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t0 + dur);
  b.g.gain.setValueAtTime(0.0001, t0);
  b.g.gain.exponentialRampToValueAtTime(o.gain ?? 0.08, t0 + 0.006);
  b.g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(b.g); osc.start(t0); osc.stop(t0 + dur + 0.05);
}
function noise(dur: number, o: { f0?: number; f1?: number; q?: number; gain?: number; delay?: number } = {}) {
  const b = bus(o.gain ?? 0.06); if (!b) return;
  const t0 = b.c.currentTime + (o.delay ?? 0);
  const n = Math.floor(b.c.sampleRate * dur);
  const buf = b.c.createBuffer(1, n, b.c.sampleRate); const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = b.c.createBufferSource(); src.buffer = buf;
  const f = b.c.createBiquadFilter(); f.type = 'bandpass';
  f.frequency.setValueAtTime(o.f0 ?? 1200, t0);
  if (o.f1) f.frequency.exponentialRampToValueAtTime(o.f1, t0 + dur);
  f.Q.value = o.q ?? 1;
  src.connect(f); f.connect(b.g); src.start(t0);
}

/* ─── hold(): a steerable voice (the tanpura drone) ────────────────────── */
type Voice = { set(f: number, cut: number, a: number): void; cut(ms: number): void; stop(): void };
const voices = new Set<Voice>();
function hold(freq: number, o: { type?: OscillatorType; detune?: number; cutoff?: number; q?: number; gain?: number } = {}, dest?: AudioNode | null): Voice | null {
  if (!soundOn) return null;
  const c = ac(); if (!c || !master) return null;
  const g = c.createGain(); g.gain.value = o.gain ?? 0; g.connect(dest ?? master);
  const osc = c.createOscillator(); osc.type = o.type ?? 'sine';
  osc.frequency.value = freq; osc.detune.value = o.detune ?? 0;
  const lp = c.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = o.cutoff ?? 900; lp.Q.value = o.q ?? 0.7;
  osc.connect(lp); lp.connect(g); osc.start();
  const tc = 0.12;
  const v: Voice = {
    set(f, cut, a) { const t = c.currentTime; osc.frequency.setTargetAtTime(f, t, tc); lp.frequency.setTargetAtTime(cut, t, tc); g.gain.setTargetAtTime(a, t, tc); },
    cut(ms) { const t = c.currentTime; g.gain.cancelScheduledValues(t); g.gain.setValueAtTime(g.gain.value, t); g.gain.linearRampToValueAtTime(0, t + ms / 1000); },
    stop() { try { osc.stop(c.currentTime + 0.06); } catch { /* already */ } try { g.disconnect(); } catch { /* gone */ } voices.delete(v); },
  };
  voices.add(v);
  return v;
}

/* pitch table — E minor pentatonic (E G A B D) */
const E2 = 82.41, B2 = 123.47, E3 = 164.81, G3 = 196, A3 = 220, B3 = 246.94, D4 = 293.66,
  E4 = 329.63, G4 = 392, A4 = 440, B4 = 493.88, D5 = 587.33, E5 = 659.25, G5 = 783.99,
  A5 = 880, B5 = 987.77, D6 = 1174.66, E6 = 1318.51, G6 = 1567.98, A6 = 1760, B6 = 1975.53;
void B2; void A3;   /* kept for future voicings */

/* ─── one-shots ───────────────────────────────────────────────────────── */

/** round start — a riffle shuffle: filtered noise bursts then the square-up tap */
export function sShuffle() {
  for (let i = 0; i < 9; i++) {
    const t = i / 9;
    noise(0.028, { f0: 2700 - 950 * t, f1: 1600 - 550 * t, q: 1.6, gain: 0.055 * (1 - 0.45 * t), delay: 0.01 + i * 0.031 });
  }
  noise(0.06, { f0: 720, f1: 250, q: 0.9, gain: 0.05, delay: 0.29 });
  tone(150, 0.07, { type: 'sine', to: 84, gain: 0.07, delay: 0.29 });
  duckTpMusic(280);
}
/** 3-2-1 countdown — pitch rises as `left` falls */
export const sCountTick = (left: number) => {
  tone(left <= 1 ? E6 : left === 2 ? B5 : G5, 0.045, { type: 'square', gain: 0.042 });
  noise(0.02, { f0: 4000, f1: 3000, q: 3, gain: 0.02 });
};
/** bets closed — a firm wooden knock, twice */
export const sKnock = () => {
  for (const d of [0, 0.085]) {
    tone(148, 0.075, { type: 'sine', to: 74, gain: 0.16, delay: d });
    noise(0.055, { f0: 320, f1: 130, q: 0.9, gain: 0.12, delay: d });
    noise(0.018, { f0: 2200, f1: 1500, q: 2, gain: 0.04, delay: d });
  }
  duckTpMusic(260);
};
/** card n of 6 flipping — snap + pluck; 'a' sits two semitones under 'b' */
export function sFlip(n: number, side: 'a' | 'b') {
  const k = Math.max(1, Math.min(6, Math.round(n)));
  const f = 440 * Math.pow(2, ((k - 1) * 1.5 + (side === 'a' ? -2 : 0)) / 12);
  noise(0.026, { f0: 4200, f1: 2600, q: 2.2, gain: 0.10 });
  noise(0.05, { f0: 900, f1: 380, q: 0.9, gain: 0.035, delay: 0.012 });
  tone(f, 0.13, { type: 'triangle', to: f * 0.985, gain: 0.055, delay: 0.02 });
  tone(f * 2, 0.07, { type: 'sine', gain: 0.018, delay: 0.02 });
  tpBedPhase('dealing', k / 6);
}
/** a hand climbed a category (1..6) — brighter and longer the higher it goes */
const PENT = [E4, G4, A4, B4, D5, E5, G5, A5, B5, D6, E6];
export function sRankUp(cat: number) {
  const c = Math.max(1, Math.min(6, Math.round(cat)));
  if (c >= 6) {                                   /* TRAIL — three-note fanfare */
    [E5, B5, E6].forEach((f, i) => tone(f, 0.34, { type: 'triangle', gain: 0.15, delay: i * 0.09 }));
    tone(G6, 0.5, { type: 'sine', gain: 0.055, delay: 0.20 });
    tone(B6, 0.45, { type: 'sine', gain: 0.03, delay: 0.26 });
    noise(0.45, { f0: 6000, f1: 10000, q: 0.8, gain: 0.04, delay: 0.18 });
    tone(E3, 0.30, { type: 'sine', to: E2, gain: 0.12, delay: 0.02 });
    duckTpMusic(600);
    return;
  }
  const n = c >= 4 ? 3 : 2;
  for (let i = 0; i < n; i++) tone(PENT[c - 1 + i * 2], 0.10 + 0.03 * c, { type: 'triangle', gain: 0.10, delay: i * 0.07 });
  noise(0.10 + 0.04 * c, { f0: 3000 + 700 * c, f1: 5000 + 900 * c, q: 1.2, gain: 0.018 + 0.005 * c, delay: 0.04 });
  duckTpMusic(180 + 40 * c);
}
/** the result plate slamming in */
export const sStamp = () => {
  tone(80, 0.14, { type: 'sine', to: 46, gain: 0.18 });
  noise(0.09, { f0: 520, f1: 180, q: 0.8, gain: 0.12 });
  noise(0.03, { f0: 3000, f1: 2000, q: 2, gain: 0.05 });
  duckTpMusic(250);
};
/** win — three tiers by amount: coin cascade over a chord */
export function sWin(amount: number) {
  const big = amount >= 2000, mid = !big && amount >= 500;
  const chord = big ? [E5, G5, B5, E6] : mid ? [E5, B5, E6] : [E5, B5];
  chord.forEach((f, i) => tone(f, big ? 0.36 : 0.28, { type: 'triangle', gain: big ? 0.16 : 0.14, delay: i * 0.075 }));
  const k = big ? 11 : mid ? 7 : 4;
  const coins = [E6, G6, A6, B6, D6];
  for (let i = 0; i < k; i++) tone(coins[i % 5], 0.12, { type: 'sine', gain: 0.09 - 0.045 * (i / Math.max(1, k - 1)), delay: 0.30 + i * 0.052 });
  noise(big ? 0.75 : 0.45, { f0: 6000, f1: big ? 11000 : 9000, q: 0.7, gain: big ? 0.055 : 0.04, delay: 0.32 });
  if (mid || big) tone(E6, 0.7, { type: 'sine', gain: 0.05, delay: 0.48 });
  if (big) { tone(B6, 0.9, { type: 'sine', gain: 0.04, delay: 0.60 }); tone(E3, 0.38, { type: 'sine', to: E2, gain: 0.15, delay: 0.28 }); }
  duckTpMusic(big ? 900 : mid ? 550 : 350);
}
/** loss — a thud, then a falling minor third */
export const sLoss = () => {
  tone(110, 0.16, { type: 'sine', to: 62, gain: 0.13 });
  noise(0.07, { f0: 300, f1: 130, q: 0.8, gain: 0.05 });
  tone(G3, 0.22, { type: 'triangle', gain: 0.055, delay: 0.10 });
  tone(E3, 0.30, { type: 'triangle', gain: 0.05, delay: 0.26 });
};
/** push — stake back; two flat notes, no verdict */
export const sPush = () => { tone(B4, 0.11, { type: 'triangle', gain: 0.075 }); tone(A4, 0.17, { type: 'triangle', gain: 0.062, delay: 0.11 }); };
/** sat this round out — barely there */
export const sSatOut = () => { noise(0.018, { f0: 3000, f1: 2200, q: 3, gain: 0.012 }); tone(A4, 0.05, { type: 'sine', gain: 0.018, delay: 0.01 }); };
export const sChip = () => { tone(B5, 0.06, { type: 'sine', gain: 0.13 }); tone(E6, 0.07, { type: 'sine', gain: 0.10, delay: 0.05 }); noise(0.03, { f0: 3000, f1: 2200, q: 2, gain: 0.09 }); };
export const sUndo = () => { tone(E6, 0.06, { type: 'sine', gain: 0.10 }); tone(B5, 0.09, { type: 'sine', to: G5, gain: 0.10, delay: 0.06 }); };
export const sDenied = () => { tone(175, 0.1, { type: 'square', gain: 0.09 }); tone(175, 0.1, { type: 'square', gain: 0.09, delay: 0.14 }); };
export const sTap = () => { tone(B5, 0.045, { type: 'sine', to: G5, gain: 0.05 }); };
let lastCrowd = 0, dealing = false;
export const sCrowd = () => {
  const now = performance.now();
  if (dealing || now < _duckedUntil || now - lastCrowd < 250) return;
  lastCrowd = now;
  noise(0.025, { f0: 4500, f1: 3500, q: 3, gain: 0.03 });
};

/* ─── the bed: "Velvet Durbar" — 92 BPM, E minor pentatonic ────────────── */
const BPM = 92, BEAT = 60 / BPM, BAR = 4 * BEAT, STEP = BAR / 16, LOOP = 4 * BAR;
const M_MASTER = 0.36, DUCK_FLOOR = 0.30;
let I = 0.50, musicLive = false, loopStart = 0, nextEv = 0, bedTimer = 0, killTimer = 0;
let leadOn = true, tabla: 'sparse' | 'accel' | 'off' = 'sparse', accel = 0, lastPhase = '';
let bedGain: GainNode | null = null, tanpura: GainNode | null = null;
let droneA: Voice | null = null, droneB: Voice | null = null, beat: OscillatorNode | null = null;
const live: { o: AudioScheduledSourceNode; end: number }[] = [];
/* one sparse sitar phrase per bar of the four-bar cycle */
const LEAD: Record<number, Record<number, number>> = {
  0: { 0: E4, 6: G4, 11: A4 }, 1: { 0: B4, 5: A4, 12: G4 },
  2: { 2: D5, 8: B4, 12: A4 }, 3: { 0: G4, 6: E4, 10: D4, 14: E4 },
};

/** sitar-like pluck: sawtooth through a resonant bandpass, bent into the note */
function sitar(c: AudioContext, out: GainNode, f: number, at: number, peak: number, decay: number) {
  const bp = c.createBiquadFilter(); bp.type = 'bandpass';
  bp.frequency.setValueAtTime(f * 3.4, at);
  bp.frequency.exponentialRampToValueAtTime(Math.max(140, f * 1.25), at + decay);
  bp.Q.value = 3.5 + 4 * I;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, at + decay);
  const o1 = c.createOscillator(); o1.type = 'sawtooth';
  o1.frequency.setValueAtTime(f * 0.955, at); o1.frequency.exponentialRampToValueAtTime(f, at + 0.075);
  const o2 = c.createOscillator(); o2.type = 'sine'; o2.detune.value = 7;
  o2.frequency.setValueAtTime(f * 1.99, at); o2.frequency.exponentialRampToValueAtTime(f * 2, at + 0.075);
  const g2 = c.createGain(); g2.gain.value = 0.3;
  o1.connect(bp); o2.connect(g2); g2.connect(bp); bp.connect(g); g.connect(out);
  const end = at + decay + 0.05;
  o1.start(at); o1.stop(end); o2.start(at); o2.stop(end);
  live.push({ o: o1, end }, { o: o2, end });
}
function bedTone(f: number, dur: number, o: { type?: OscillatorType; to?: number; gain?: number; delay?: number }) {
  const c = ctx; if (!c || !bedGain || !musicLive) return;
  const t0 = c.currentTime + (o.delay ?? 0);
  const osc = c.createOscillator(); osc.type = o.type ?? 'sine'; osc.frequency.setValueAtTime(f, t0);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t0 + dur);
  const g = c.createGain(); g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(o.gain ?? 0.05, t0 + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g); g.connect(bedGain); osc.start(t0); osc.stop(t0 + dur + 0.05);
  live.push({ o: osc, end: t0 + dur + 0.05 });
}
function bedNoise(dur: number, o: { f0: number; f1: number; q: number; gain: number; delay?: number }) {
  const c = ctx; if (!c || !bedGain || !musicLive) return;
  const t0 = c.currentTime + (o.delay ?? 0);
  const n = Math.floor(c.sampleRate * dur); const buf = c.createBuffer(1, n, c.sampleRate); const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = c.createBufferSource(); src.buffer = buf;
  const f = c.createBiquadFilter(); f.type = 'bandpass';
  f.frequency.setValueAtTime(o.f0, t0); f.frequency.exponentialRampToValueAtTime(o.f1, t0 + dur); f.Q.value = o.q;
  const g = c.createGain(); g.gain.value = o.gain;
  src.connect(f); f.connect(g); g.connect(bedGain); src.start(t0);
  live.push({ o: src, end: t0 + dur });
}
/* tabla strokes */
const ghe = (d: number, g: number) => { bedTone(96, 0.24, { type: 'sine', to: 56, gain: g, delay: d }); bedNoise(0.03, { f0: 900, f1: 400, q: 1.2, gain: g * 0.45, delay: d }); };
const tin = (d: number, g: number, hi = false) => bedNoise(0.035, { f0: hi ? 3400 : 2300, f1: hi ? 2100 : 1450, q: 5, gain: g, delay: d });

function pump() {
  const c = ctx; if (!c || !musicLive || !bedGain) return;
  const horizon = c.currentTime + 0.20;
  const earliest = c.currentTime - STEP;
  if (nextEv < earliest) nextEv = loopStart + Math.ceil((earliest - loopStart) / STEP) * STEP;
  while (nextEv < horizon) {
    const pos = (nextEv - loopStart) % LOOP;
    const bar = Math.floor(pos / BAR) % 4, s = Math.round((pos % BAR) / STEP) % 16;
    const d = Math.max(0, nextEv - c.currentTime), at = nextEv;
    if (tabla !== 'off') {
      const tg = tabla === 'accel' ? 0.030 + 0.030 * accel : 0.026;
      if (s === 0 || s === 8) ghe(d, 0.055 + 0.02 * I);                      /* beats 1 and 3 */
      if (tabla === 'sparse') { if (s === 6 || s === 14) tin(d, tg, s === 6); }
      else { const every = accel < 0.34 ? 8 : accel < 0.67 ? 4 : 2; if (s % every === 0 && s !== 0 && s !== 8) tin(d, tg, s % 8 === 4); }
    }
    if (leadOn && I >= 0.35) { const f = LEAD[bar][s]; if (f) sitar(c, bedGain, f, at, 0.030 + 0.028 * I, 0.85 + 0.5 * I); }
    nextEv += STEP;
  }
  if (live.length > 128) { const now = c.currentTime; for (let i = live.length - 1; i >= 0; i--) if (live[i].end < now) live.splice(i, 1); }
}
/** result — a bright chord swells for ~1.2 s, then the bed goes quiet */
function swell() {
  const c = ctx; if (!c || !bedGain || !musicLive) return;
  const t0 = c.currentTime;
  const lp = c.createBiquadFilter(); lp.type = 'lowpass'; lp.Q.value = 1.1;
  lp.frequency.setValueAtTime(520, t0);
  lp.frequency.exponentialRampToValueAtTime(4400, t0 + 0.45);
  lp.frequency.exponentialRampToValueAtTime(700, t0 + 1.2);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.08, t0 + 0.35);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.2);
  lp.connect(g); g.connect(bedGain);
  for (const f of [E3, B3, E4, G4, B4]) {
    const o = c.createOscillator(); o.type = 'triangle'; o.frequency.value = f; o.detune.value = Math.random() * 8 - 4;
    o.connect(lp); o.start(t0); o.stop(t0 + 1.3); live.push({ o, end: t0 + 1.3 });
  }
}
function applyDrone() {
  const cut = 420 * Math.pow(2, 1.9 * I), a = 0.055 + 0.05 * I;
  droneA?.set(E2, cut, a); droneB?.set(E2 * 1.0055, cut, a * 0.85);
}
export function tpBedPhase(phase: 'betting' | 'dealing' | 'result' | 'intermission', extra = 0) {
  const e = Math.min(1, Math.max(0, extra)), fresh = phase !== lastPhase;
  lastPhase = phase;
  if (phase === 'betting') { I = 0.55; leadOn = true; tabla = 'sparse'; accel = 0; dealing = false; }
  else if (phase === 'dealing') { I = 0.32; leadOn = false; tabla = 'accel'; accel = e; dealing = true; }
  else if (phase === 'result') { I = 0.18; leadOn = false; tabla = 'off'; accel = 0; dealing = false; if (fresh) swell(); }
  else { I = 0.24; leadOn = false; tabla = 'off'; accel = 0; dealing = false; }
  applyDrone();
}

export function startTpMusic() {
  if (musicLive || !soundOn || !musicOn) return;
  const c = ac(); if (!c || !master) return;
  window.clearTimeout(killTimer); killTimer = 0;
  bedGain = c.createGain(); bedGain.gain.value = 0.0001; bedGain.connect(master);
  bedGain.gain.exponentialRampToValueAtTime(M_MASTER, c.currentTime + BAR);
  /* tanpura: two detuned sines under a slow amplitude beat */
  tanpura = c.createGain(); tanpura.gain.value = 0.78; tanpura.connect(bedGain);
  beat = c.createOscillator(); beat.type = 'sine'; beat.frequency.value = 0.14;
  const depth = c.createGain(); depth.gain.value = 0.22;
  beat.connect(depth); depth.connect(tanpura.gain); beat.start();
  droneA = hold(E2, { type: 'sine', cutoff: 700, gain: 0 }, tanpura);
  droneB = hold(E2 * 1.0055, { type: 'sine', detune: 4, cutoff: 700, gain: 0 }, tanpura);
  musicLive = true; loopStart = c.currentTime + 0.10; nextEv = loopStart;
  applyDrone(); pump(); bedTimer = window.setInterval(pump, 50);
}
export function stopTpMusic() {
  musicLive = false; window.clearInterval(bedTimer); bedTimer = 0; _duckedUntil = 0; lastPhase = '';
  droneA?.stop(); droneB?.stop(); droneA = droneB = null;
  try { beat?.stop(); } catch { /* already */ } beat = null;
  const c = ctx, g = bedGain, tp = tanpura, mine = live.splice(0); bedGain = null; tanpura = null;
  if (!c || !g) return;
  const now = c.currentTime;
  g.gain.cancelScheduledValues(now); g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
  for (const nd of mine) { try { nd.o.stop(now + 0.18); } catch { /* done */ } }
  window.clearTimeout(killTimer);
  killTimer = window.setTimeout(() => { killTimer = 0; try { tp?.disconnect(); } catch { /* gone */ } try { g.disconnect(); } catch { /* gone */ } }, 420);
}
export function duckTpMusic(ms: number) {
  const c = ctx; if (!c || !bedGain || !musicLive) return;
  const t = c.currentTime;
  bedGain.gain.cancelScheduledValues(t); bedGain.gain.setValueAtTime(Math.max(bedGain.gain.value, 0.0001), t);
  bedGain.gain.exponentialRampToValueAtTime(M_MASTER * DUCK_FLOOR, t + 0.05);
  bedGain.gain.setValueAtTime(M_MASTER * DUCK_FLOOR, t + ms / 1000);
  bedGain.gain.exponentialRampToValueAtTime(M_MASTER, t + ms / 1000 + 0.5);
  _duckedUntil = performance.now() + ms + 500;
}
export function bindTpVisibility() {
  const h = () => { const c = ctx; if (!c) return; if (document.hidden) { void c.suspend(); } else if (soundOn) { void c.resume(); } };
  document.addEventListener('visibilitychange', h);
  return () => document.removeEventListener('visibilitychange', h);
}
